/**
 * RideWorkflowModal.js - Complete Ride/Driver Workflow
 * Steps: Accept → Navigate to Pickup → Arrived → Start Trip → In Progress → Complete → Payment
 */
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ChevronRight, ChevronLeft, MapPin, Clock, Navigation, Car,
  CheckCircle, AlertTriangle, User, Phone, DollarSign, CreditCard,
  Loader2, Timer, Route, Flag, Banknote, MessageSquare
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const RIDE_STEPS = {
  OVERVIEW: 'overview',
  NAVIGATE_PICKUP: 'navigate_pickup',
  ARRIVED: 'arrived',
  PASSENGER_PICKUP: 'passenger_pickup',
  TRIP_PROGRESS: 'trip_progress',
  COMPLETE: 'complete',
  PAYMENT: 'payment'
};

const RideWorkflowModal = ({ isOpen, onClose, ride, driver, onWorkflowComplete }) => {
  const [currentStep, setCurrentStep] = useState(RIDE_STEPS.OVERVIEW);
  const [loading, setLoading] = useState(false);
  
  // Navigation state
  const [isNavigating, setIsNavigating] = useState(false);
  const [arrivedAtPickup, setArrivedAtPickup] = useState(false);
  const [arrivalTime, setArrivalTime] = useState(null);
  
  // Trip state
  const [passengerPickedUp, setPassengerPickedUp] = useState(false);
  const [pickupTime, setPickupTime] = useState(null);
  const [tripStarted, setTripStarted] = useState(false);
  const [tripStartTime, setTripStartTime] = useState(null);
  const [tripCompleted, setTripCompleted] = useState(false);
  const [tripEndTime, setTripEndTime] = useState(null);
  
  // Mileage and time tracking
  const [startMileage, setStartMileage] = useState('');
  const [endMileage, setEndMileage] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef(null);
  
  // Notes
  const [notes, setNotes] = useState('');
  
  // Payment state
  const [paymentCollected, setPaymentCollected] = useState(false);
  const [tipAmount, setTipAmount] = useState(0);
  
  // Location tracking
  const [currentLocation, setCurrentLocation] = useState(null);

  // Start timer when trip starts
  useEffect(() => {
    if (tripStarted && !tripCompleted) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [tripStarted, tripCompleted]);

  // Format elapsed time
  const formatElapsedTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Open navigation app
  const openNavigation = (destination) => {
    const address = encodeURIComponent(destination);
    // Try Google Maps first, fallback to Apple Maps on iOS
    const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${address}`;
    window.open(googleMapsUrl, '_blank');
    setIsNavigating(true);
    toast.success('Navigation opened');
  };

  // Confirm arrival at pickup
  const confirmArrival = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setArrivedAtPickup(true);
          setArrivalTime(new Date().toISOString());
          toast.success('Arrival confirmed - waiting for passenger');
        },
        () => {
          setArrivedAtPickup(true);
          setArrivalTime(new Date().toISOString());
          toast.info('Arrival confirmed manually');
        }
      );
    } else {
      setArrivedAtPickup(true);
      setArrivalTime(new Date().toISOString());
    }
  };

  // Pick up passenger
  const pickUpPassenger = () => {
    setPassengerPickedUp(true);
    setPickupTime(new Date().toISOString());
    toast.success('Passenger picked up');
  };

  // Start trip
  const startTrip = () => {
    setTripStarted(true);
    setTripStartTime(new Date().toISOString());
    setElapsedTime(0);
    toast.success('Trip started - drive safely!');
  };

  // Complete trip
  const completeTrip = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setTripCompleted(true);
    setTripEndTime(new Date().toISOString());
    toast.success('Trip completed');
  };

  // Confirm payment
  const confirmPayment = async () => {
    setLoading(true);
    try {
      const completionData = {
        ride_id: ride.id,
        arrival_time: arrivalTime,
        pickup_time: pickupTime,
        trip_start_time: tripStartTime,
        trip_end_time: tripEndTime,
        duration_seconds: elapsedTime,
        start_mileage: startMileage ? parseFloat(startMileage) : null,
        end_mileage: endMileage ? parseFloat(endMileage) : null,
        actual_miles: startMileage && endMileage ? parseFloat(endMileage) - parseFloat(startMileage) : ride.estimated_miles,
        notes: notes,
        tip_amount: tipAmount,
        payment_collected: true,
        completed_at: new Date().toISOString()
      };

      // In real implementation, send to API
      // await api.post(`/api/rides/${ride.id}/complete`, completionData);

      toast.success('Ride completed successfully!');
      onWorkflowComplete?.(completionData);
      onClose();
    } catch (error) {
      toast.error('Failed to complete ride');
    } finally {
      setLoading(false);
    }
  };

  // Navigation helpers
  const goToNextStep = () => {
    const stepOrder = Object.values(RIDE_STEPS);
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      setCurrentStep(stepOrder[currentIndex + 1]);
    }
  };

  const goToPrevStep = () => {
    const stepOrder = Object.values(RIDE_STEPS);
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(stepOrder[currentIndex - 1]);
    }
  };

  const getStepProgress = () => {
    const stepOrder = Object.values(RIDE_STEPS);
    const currentIndex = stepOrder.indexOf(currentStep);
    return ((currentIndex + 1) / stepOrder.length) * 100;
  };

  // Get payment method display
  const getPaymentMethodBadge = () => {
    const method = ride?.payment_method || 'card';
    if (method === 'cash') {
      return <Badge className="bg-green-500/20 text-green-400">Cash Payment</Badge>;
    }
    return <Badge className="bg-blue-500/20 text-blue-400">Card Payment</Badge>;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
        data-testid="ride-workflow-modal"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[var(--card)] border border-[var(--border)] rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="p-4 border-b border-[var(--border)] bg-gradient-to-r from-purple-600/20 to-pink-600/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/20">
                  <Car className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h2 className="font-semibold text-white">Ride Workflow</h2>
                  <p className="text-xs text-gray-400 font-mono">{ride?.reference || ride?.id?.slice(0, 8)}</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Progress Bar */}
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-400 mb-1">
                <span>Progress</span>
                <span>{Math.round(getStepProgress())}%</span>
              </div>
              <div className="h-2 bg-[var(--background)] rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${getStepProgress()}%` }}
                />
              </div>
            </div>

            {/* Timer Display */}
            {tripStarted && !tripCompleted && (
              <div className="mt-2 flex items-center justify-center gap-2 text-green-400 text-lg font-mono">
                <Timer className="w-5 h-5 animate-pulse" />
                <span>{formatElapsedTime(elapsedTime)}</span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <AnimatePresence mode="wait">
              {/* OVERVIEW STEP */}
              {currentStep === RIDE_STEPS.OVERVIEW && (
                <motion.div
                  key="overview"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white">Ride Details</h3>
                  
                  {/* Passenger Info */}
                  <div className="p-3 rounded-lg bg-[var(--background)] space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-white">
                        <User className="w-4 h-4 text-gray-400" />
                        <span>{ride?.customer_name || ride?.passenger_name || 'Passenger'}</span>
                      </div>
                      {getPaymentMethodBadge()}
                    </div>
                    {ride?.customer_phone && (
                      <a 
                        href={`tel:${ride.customer_phone}`}
                        className="flex items-center gap-2 text-blue-400 hover:text-blue-300"
                      >
                        <Phone className="w-4 h-4" />
                        <span>{ride.customer_phone}</span>
                      </a>
                    )}
                  </div>

                  {/* Pickup Location */}
                  <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                    <p className="text-xs text-green-400 mb-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> PICKUP
                    </p>
                    <p className="text-white">{ride?.pickup_address || 'Pickup location'}</p>
                  </div>

                  {/* Dropoff Location */}
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <p className="text-xs text-red-400 mb-1 flex items-center gap-1">
                      <Flag className="w-3 h-3" /> DROPOFF
                    </p>
                    <p className="text-white">{ride?.dropoff_address || 'Dropoff location'}</p>
                  </div>

                  {/* Stops */}
                  {ride?.stops?.length > 0 && (
                    <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                      <p className="text-xs text-yellow-400 mb-1">STOPS ({ride.stops.length})</p>
                      {ride.stops.map((stop, idx) => (
                        <p key={idx} className="text-white text-sm">• {stop.address || stop}</p>
                      ))}
                    </div>
                  )}

                  {/* Fare Info */}
                  <div className="p-3 rounded-lg bg-[var(--background)] flex items-center justify-between">
                    <span className="text-gray-400">Estimated Fare</span>
                    <span className="text-2xl font-bold text-green-400">
                      ${(ride?.estimated_fare || ride?.fare || 0).toFixed(2)}
                    </span>
                  </div>

                  {/* Ride Details */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 rounded bg-[var(--background)] text-center">
                      <p className="text-xs text-gray-400">Distance</p>
                      <p className="text-white font-medium">{ride?.estimated_miles?.toFixed(1) || '0'} mi</p>
                    </div>
                    <div className="p-2 rounded bg-[var(--background)] text-center">
                      <p className="text-xs text-gray-400">Est. Time</p>
                      <p className="text-white font-medium">{ride?.estimated_duration_minutes || '0'} min</p>
                    </div>
                  </div>

                  {ride?.notes && (
                    <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                      <p className="text-xs text-yellow-400 mb-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> NOTES
                      </p>
                      <p className="text-white text-sm">{ride.notes}</p>
                    </div>
                  )}
                </motion.div>
              )}

              {/* NAVIGATE TO PICKUP */}
              {currentStep === RIDE_STEPS.NAVIGATE_PICKUP && (
                <motion.div
                  key="navigate"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Navigation className="w-5 h-5 text-blue-400" />
                    Navigate to Pickup
                  </h3>

                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                    <p className="text-green-400 font-medium mb-2">Pickup Location</p>
                    <p className="text-white">{ride?.pickup_address}</p>
                  </div>

                  <Button 
                    onClick={() => openNavigation(ride?.pickup_address)}
                    className="w-full bg-blue-600 hover:bg-blue-700 h-14 text-lg"
                  >
                    <Navigation className="w-5 h-5 mr-2" />
                    Open Navigation
                  </Button>

                  {isNavigating && (
                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <p className="text-blue-300 text-sm text-center">
                        Navigation opened in external app. 
                        Drive safely to the pickup location.
                      </p>
                    </div>
                  )}

                  <div className="pt-4">
                    <p className="text-gray-400 text-sm text-center mb-3">
                      When you arrive at the pickup location:
                    </p>
                    <Button 
                      onClick={confirmArrival}
                      disabled={arrivedAtPickup}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      {arrivedAtPickup ? (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Arrived at {arrivalTime ? new Date(arrivalTime).toLocaleTimeString() : ''}
                        </>
                      ) : (
                        <>
                          <MapPin className="w-4 h-4 mr-2" />
                          Confirm Arrival
                        </>
                      )}
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* ARRIVED - WAITING FOR PASSENGER */}
              {currentStep === RIDE_STEPS.ARRIVED && (
                <motion.div
                  key="arrived"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-yellow-400" />
                    Waiting for Passenger
                  </h3>

                  <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-center">
                    <p className="text-yellow-400 font-medium">You have arrived!</p>
                    <p className="text-white mt-2">{ride?.pickup_address}</p>
                    <p className="text-gray-400 text-sm mt-2">
                      Arrived at {arrivalTime ? new Date(arrivalTime).toLocaleTimeString() : ''}
                    </p>
                  </div>

                  {/* Contact Passenger */}
                  <div className="grid grid-cols-2 gap-2">
                    {ride?.customer_phone && (
                      <a 
                        href={`tel:${ride.customer_phone}`}
                        className="flex items-center justify-center gap-2 p-3 rounded-lg bg-blue-500/20 text-blue-400"
                      >
                        <Phone className="w-4 h-4" />
                        Call
                      </a>
                    )}
                    <Button variant="outline" className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Message
                    </Button>
                  </div>

                  <Button 
                    onClick={pickUpPassenger}
                    disabled={passengerPickedUp}
                    className="w-full bg-green-600 hover:bg-green-700 h-14 text-lg"
                  >
                    {passengerPickedUp ? (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Passenger Picked Up
                      </>
                    ) : (
                      <>
                        <User className="w-5 h-5 mr-2" />
                        Passenger Picked Up
                      </>
                    )}
                  </Button>
                </motion.div>
              )}

              {/* PASSENGER PICKUP - START TRIP */}
              {currentStep === RIDE_STEPS.PASSENGER_PICKUP && (
                <motion.div
                  key="pickup"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Car className="w-5 h-5 text-green-400" />
                    Start Trip
                  </h3>

                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20 text-center">
                    <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-2" />
                    <p className="text-green-400 font-medium">Passenger on board!</p>
                  </div>

                  {/* Destination */}
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <p className="text-xs text-red-400 mb-1">DESTINATION</p>
                    <p className="text-white">{ride?.dropoff_address}</p>
                  </div>

                  {/* Optional: Start Mileage */}
                  <div>
                    <Label className="text-gray-400">Start Mileage (optional)</Label>
                    <Input
                      type="number"
                      value={startMileage}
                      onChange={(e) => setStartMileage(e.target.value)}
                      placeholder="Enter odometer reading"
                      className="mt-1 bg-[var(--background)]"
                    />
                  </div>

                  <Button 
                    onClick={() => openNavigation(ride?.dropoff_address)}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    Navigate to Destination
                  </Button>

                  <Button 
                    onClick={startTrip}
                    disabled={tripStarted}
                    className="w-full bg-purple-600 hover:bg-purple-700 h-14 text-lg"
                  >
                    {tripStarted ? (
                      <>
                        <Timer className="w-5 h-5 mr-2 animate-pulse" />
                        Trip in Progress
                      </>
                    ) : (
                      <>
                        <Route className="w-5 h-5 mr-2" />
                        Start Trip
                      </>
                    )}
                  </Button>
                </motion.div>
              )}

              {/* TRIP IN PROGRESS */}
              {currentStep === RIDE_STEPS.TRIP_PROGRESS && (
                <motion.div
                  key="progress"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Route className="w-5 h-5 text-purple-400" />
                    Trip in Progress
                  </h3>

                  {/* Timer */}
                  <div className="p-6 rounded-lg bg-purple-500/10 border border-purple-500/20 text-center">
                    <p className="text-gray-400 text-sm">Trip Duration</p>
                    <p className="text-4xl font-mono text-purple-400 mt-2">
                      {formatElapsedTime(elapsedTime)}
                    </p>
                  </div>

                  {/* Destination */}
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                    <p className="text-xs text-red-400 mb-1">HEADING TO</p>
                    <p className="text-white">{ride?.dropoff_address}</p>
                  </div>

                  {/* Stops (if any) */}
                  {ride?.stops?.length > 0 && (
                    <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                      <p className="text-xs text-yellow-400 mb-2">STOPS</p>
                      {ride.stops.map((stop, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-white text-sm py-1">
                          <div className="w-5 h-5 rounded-full bg-yellow-500/20 flex items-center justify-center text-xs">
                            {idx + 1}
                          </div>
                          {stop.address || stop}
                        </div>
                      ))}
                    </div>
                  )}

                  <Button 
                    onClick={completeTrip}
                    disabled={tripCompleted}
                    className="w-full bg-green-600 hover:bg-green-700 h-14 text-lg"
                  >
                    {tripCompleted ? (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Trip Completed
                      </>
                    ) : (
                      <>
                        <Flag className="w-5 h-5 mr-2" />
                        Complete Trip
                      </>
                    )}
                  </Button>
                </motion.div>
              )}

              {/* COMPLETE - NOTES */}
              {currentStep === RIDE_STEPS.COMPLETE && (
                <motion.div
                  key="complete"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Trip Complete
                  </h3>

                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20 text-center">
                    <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-2" />
                    <p className="text-green-400 font-medium">Passenger dropped off!</p>
                    <p className="text-gray-400 text-sm mt-1">
                      Trip duration: {formatElapsedTime(elapsedTime)}
                    </p>
                  </div>

                  {/* End Mileage */}
                  <div>
                    <Label className="text-gray-400">End Mileage (optional)</Label>
                    <Input
                      type="number"
                      value={endMileage}
                      onChange={(e) => setEndMileage(e.target.value)}
                      placeholder="Enter odometer reading"
                      className="mt-1 bg-[var(--background)]"
                    />
                  </div>

                  {/* Trip Notes */}
                  <div>
                    <Label className="text-gray-400">Trip Notes (optional)</Label>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Any notes about the trip..."
                      className="mt-1 bg-[var(--background)]"
                      rows={2}
                    />
                  </div>

                  {/* Trip Summary */}
                  <div className="p-3 rounded-lg bg-[var(--background)] space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Duration</span>
                      <span className="text-white">{formatElapsedTime(elapsedTime)}</span>
                    </div>
                    {startMileage && endMileage && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Actual Miles</span>
                        <span className="text-white">{(parseFloat(endMileage) - parseFloat(startMileage)).toFixed(1)} mi</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* PAYMENT */}
              {currentStep === RIDE_STEPS.PAYMENT && (
                <motion.div
                  key="payment"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-400" />
                    Payment
                  </h3>

                  {/* Fare Breakdown */}
                  <div className="p-4 rounded-lg bg-[var(--background)] space-y-3">
                    <div className="flex justify-between text-lg">
                      <span className="text-gray-400">Ride Fare</span>
                      <span className="text-white font-bold">${(ride?.estimated_fare || 0).toFixed(2)}</span>
                    </div>
                    
                    {/* Payment Method */}
                    <div className="flex justify-between items-center pt-2 border-t border-[var(--border)]">
                      <span className="text-gray-400">Payment Method</span>
                      {getPaymentMethodBadge()}
                    </div>
                  </div>

                  {/* Cash Collection (if cash payment) */}
                  {ride?.payment_method === 'cash' && (
                    <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                      <div className="flex items-center gap-2 text-yellow-400 mb-2">
                        <Banknote className="w-5 h-5" />
                        <span className="font-medium">Cash Payment Required</span>
                      </div>
                      <p className="text-white text-2xl font-bold">
                        Collect: ${(ride?.estimated_fare || 0).toFixed(2)}
                      </p>
                    </div>
                  )}

                  {/* Card Payment Confirmation */}
                  {ride?.payment_method !== 'cash' && (
                    <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <div className="flex items-center gap-2 text-blue-400 mb-2">
                        <CreditCard className="w-5 h-5" />
                        <span className="font-medium">Card Payment</span>
                      </div>
                      <p className="text-gray-300 text-sm">
                        Payment will be automatically processed
                      </p>
                    </div>
                  )}

                  <Button
                    onClick={confirmPayment}
                    disabled={loading}
                    className="w-full bg-green-600 hover:bg-green-700 h-14 text-lg"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="w-5 h-5 mr-2" />
                    )}
                    {ride?.payment_method === 'cash' ? 'Confirm Cash Collected' : 'Complete Ride'}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-[var(--border)] flex justify-between">
            <Button
              variant="outline"
              onClick={goToPrevStep}
              disabled={currentStep === RIDE_STEPS.OVERVIEW}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            {currentStep !== RIDE_STEPS.PAYMENT && (
              <Button onClick={goToNextStep}>
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RideWorkflowModal;
